<template>
  <div class="flex flex-col items-center justify-center p-2 w-full">
    <!-- Dropzone -->
    <div v-if="!uploadedFile"  
      @click="fileInput.click()"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
      class="w-full max-w-4xl transition-all duration-500 cursor-pointer rounded-3xl flex flex-col items-center justify-center text-center relative overflow-hidden group"
      :class="[isDragging ? 'scale-[1.01]' : '']">
      
      <!-- Animated gradient border -->
      <div class="absolute inset-0 rounded-3xl p-[1.5px] overflow-hidden">
        <div class="absolute inset-0 rounded-3xl transition-opacity duration-500"
          :class="isDragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'"
          style="background: conic-gradient(from 0deg, #D01020, #104060, #D01020);"></div>
        <div class="absolute inset-0 rounded-3xl transition-opacity duration-500"
          :class="isDragging ? 'opacity-0' : 'opacity-100 group-hover:opacity-0'"
          style="background: #E0E0E0;"></div>
      </div>

      <!-- Inner card -->
      <div class="relative w-full rounded-[22px] py-12 px-10 transition-all duration-500 flex flex-col items-center bg-white border border-gray-100 shadow-lg">
        
        <!-- Subtle decorative glow -->
        <div class="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-32 rounded-full opacity-0 group-hover:opacity-40 transition-opacity duration-700 blur-3xl pointer-events-none" style="background: radial-gradient(circle, #D01020 0%, transparent 70%);"></div>

        <div class="relative z-10 flex flex-col items-center">
          <!-- PDF Icon -->
          <div class="w-12 h-12 mb-2 flex items-center justify-center transition-all duration-300">
            <img :src="'/icon.png'" alt="PDF Icon" class="w-full h-full object-contain" />
          </div>
          
          <!-- Heading -->
          <span class="text-[19px] font-bold text-[#104060] mb-1">Convierte tu PDF a Word</span>
          <p class="text-gray-400 text-[14px] mb-4 max-w-sm">Selecciona o arrastra tu archivo PDF para extraer y convertir el texto a formato de Word editable</p>

          <!-- Upload Button -->
          <button
            @click.stop="fileInput.click()"
            class="text-white text-[14px] font-semibold px-6 py-2.5 rounded-xl active:scale-[0.97] transition-all duration-200 flex items-center gap-2.5 shadow-lg shadow-red-200/60 hover:shadow-xl hover:shadow-red-300/60 bg-[#D01020] hover:bg-[#B01010]">
            <Upload :size="16" />
            <span>Seleccionar Archivo PDF</span>
          </button>
          
          <!-- Divider -->
          <div class="mt-7 mb-1 flex items-center gap-3 w-full max-w-xs">
            <div class="h-px flex-1 bg-gray-200"></div>
            <kbd class="px-2 py-0.5 bg-gray-50 border border-gray-200 rounded text-[10px] font-mono text-gray-500">CTRL + V</kbd>
            <p class="text-[10px] text-gray-400 font-medium uppercase tracking-widest">o arrastra aquí</p>
            <div class="h-px flex-1 bg-gray-200"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Converter Panel -->
    <div v-else class="bg-white rounded-3xl w-full max-w-2xl shadow-xl border border-gray-100 p-8 flex flex-col items-center relative overflow-hidden">
      <!-- Top decorative bar -->
      <div class="absolute top-0 left-0 right-0 h-1.5 bg-[#D01020]"></div>

      <!-- File Details Card -->
      <div class="w-full bg-[#F8FAFC] rounded-2xl p-5 border border-slate-100 flex items-center gap-4 mb-8">
        <div class="w-12 h-12 bg-rose-50 rounded-xl flex items-center justify-center flex-shrink-0">
          <FileText :size="24" class="text-[#D01020]" />
        </div>
        <div class="flex-1 min-w-0">
          <h3 class="text-slate-800 font-bold text-base truncate">{{ uploadedFile.name }}</h3>
          <p class="text-slate-400 text-xs mt-0.5">
            {{ formatBytes(uploadedFile.size) }} • {{ pageCount }} {{ pageCount === 1 ? 'página' : 'páginas' }}
          </p>
        </div>
        <button 
          @click="resetAll" 
          class="p-2 text-slate-400 hover:text-[#D01020] hover:bg-rose-50 rounded-lg transition-colors"
          title="Eliminar archivo"
        >
          <X :size="20" />
        </button>
      </div>

      <!-- Conversion Progress Indicator -->
      <div v-if="isProcessing" class="w-full flex flex-col items-center mb-6">
        <div class="relative w-16 h-16 mb-4">
          <div class="absolute inset-0 rounded-full border-[3px] border-red-50"></div>
          <div class="absolute inset-0 rounded-full border-[3px] border-transparent border-t-[#D01020] animate-spin"></div>
        </div>
        <p class="text-slate-700 font-bold text-base">Convirtiendo documento...</p>
        <p class="text-slate-400 text-sm mt-1">
          Procesando página {{ currentPageProcessed }} de {{ pageCount }}
        </p>
        <!-- Progress bar -->
        <div class="w-full bg-slate-100 h-2 rounded-full mt-4 overflow-hidden">
          <div 
            class="bg-[#D01020] h-full transition-all duration-300 rounded-full"
            :style="{ width: `${(currentPageProcessed / pageCount) * 100}%` }"
          ></div>
        </div>
      </div>

      <!-- Conversion Completed Info -->
      <div v-if="isCompleted && !isProcessing" class="w-full flex flex-col items-center mb-6 text-center">
        <div class="w-16 h-16 bg-green-50 text-green-500 rounded-full flex items-center justify-center mb-4">
          <Check :size="32" strokeWidth="3" />
        </div>
        <p class="text-slate-800 font-bold text-lg">¡Conversión Completada!</p>
        <p class="text-slate-500 text-sm mt-1">Tu archivo Word editable está listo para ser descargado.</p>
      </div>

      <!-- Action Buttons -->
      <div class="flex items-center gap-3 w-full mt-2">
        <button 
          @click="resetAll" 
          :disabled="isProcessing"
          class="flex-1 bg-white hover:bg-slate-50 text-slate-700 py-3 px-6 rounded-xl border border-slate-200 font-semibold transition-all flex items-center justify-center gap-2"
        >
          <RotateCcw :size="18" />
          <span>Reiniciar</span>
        </button>

        <button 
          @click="convertToWord" 
          :disabled="isProcessing"
          class="flex-[2] bg-[#D01020] hover:bg-[#b01010] text-white py-3 px-6 rounded-xl font-bold transition-all shadow-lg shadow-rose-200 flex items-center justify-center gap-2 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:pointer-events-none"
        >
          <Download :size="18" />
          <span>{{ t('download_doc') }}</span>
        </button>
      </div>
    </div>

    <!-- Hidden file input -->
    <input
      type="file"
      ref="fileInput"
      accept="application/pdf"
      class="hidden"
      @change="onFileChange"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import * as pdfjsLib from 'pdfjs-dist'
import { Upload, X, FileText, RotateCcw, Download, Check } from 'lucide-vue-next'

const props = defineProps({
  translations: {
    type: Object,
    required: true
  }
})

const t = (key, replacements = {}) => {
  let translation = (props.translations && props.translations[key]) || key
  for (const [k, v] of Object.entries(replacements)) {
    translation = translation.replace(`:${k}`, v)
  }
  return translation
}

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@4.10.38/build/pdf.worker.min.mjs`;

const fileInput = ref(null)
const uploadedFile = ref(null)
const isDragging = ref(false)
const isProcessing = ref(ref(false))
const isCompleted = ref(false)
const pageCount = ref(0)
const currentPageProcessed = ref(0)
const pdfDocRef = ref(null)

const handleDrop = (e) => {
  isDragging.value = false
  const files = Array.from(e.dataTransfer.files)
  if (files.length > 0) {
    processFile(files[0])
  }
}

const handlePaste = (e) => {
  const items = e.clipboardData?.items
  if (!items) return
  
  for (let item of items) {
    if (item.type === 'application/pdf' || item.type.startsWith('application/')) {
      const file = item.getAsFile()
      if (file && file.type === 'application/pdf') {
        processFile(file)
        break
      }
    }
  }
}

const onFileChange = (e) => {
  const files = Array.from(e.target.files)
  if (files.length) processFile(files[0])
  e.target.value = ''
}

const processFile = async (file) => {
  if (file.type !== 'application/pdf') {
    alert('Por favor, selecciona un archivo PDF.')
    return
  }

  uploadedFile.value = file
  isCompleted.value = false
  isProcessing.value = true
  currentPageProcessed.value = 0

  try {
    const buffer = await file.arrayBuffer()
    const pdfjsDoc = await pdfjsLib.getDocument({ data: new Uint8Array(buffer) }).promise
    pdfDocRef.value = pdfjsDoc
    pageCount.value = pdfjsDoc.numPages
  } catch (error) {
    console.error('Error loading PDF:', error)
    alert('Error al leer el archivo PDF. Inténtalo de nuevo.')
    resetAll()
  } finally {
    isProcessing.value = false
  }
}

const convertToWord = async () => {
  if (!pdfDocRef.value || !uploadedFile.value) return

  isProcessing.value = true
  currentPageProcessed.value = 0
  isCompleted.value = false

  console.log('Starting PDF to Word conversion for:', uploadedFile.value.name)

  try {
    const lines = []
    const pdfjsDoc = pdfDocRef.value

    for (let i = 1; i <= pdfjsDoc.numPages; i++) {
      console.log(`Processing page ${i} of ${pdfjsDoc.numPages}`)
      currentPageProcessed.value = i
      
      // Let UI update
      await new Promise(resolve => setTimeout(resolve, 30))

      const page = await pdfjsDoc.getPage(i)
      console.log(`Page ${i} loaded successfully. Extracting text content...`)
      
      const textContent = await page.getTextContent()
      const items = (textContent && textContent.items) || []
      console.log(`Extracted ${items.length} items from page ${i}`)
      
      let pageText = ''
      let lastY = null

      for (const item of items) {
        if (!item || !item.transform) continue
        // If Y coordinate has changed significantly, we insert a line break
        if (lastY !== null && Math.abs(item.transform[5] - lastY) > 8) {
          pageText += '<br/>'
        }
        pageText += (item.str || '') + ' '
        lastY = item.transform[5]
      }

      lines.push(`
        <div class="page" style="margin-bottom: 40px; page-break-after: always;">
          <h2 style="color: #104060; font-family: 'Arial', sans-serif; font-size: 16px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; margin-bottom: 12px; margin-top: 24px;">Página ${i}</h2>
          <p style="font-family: 'Arial', sans-serif; font-size: 12px; line-height: 1.6; color: #334155; text-align: justify; white-space: pre-wrap;">${pageText}</p>
        </div>
      `)
    }

    console.log('Text extraction completed for all pages. Generating HTML document...')
    const docContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <title>${uploadedFile.value.name.replace(/\.pdf$/i, '')}</title>
        <meta charset="utf-8">
        <!--[if gte mso 9]>
        <xml>
          <w:WordDocument>
            <w:View>Print</w:View>
            <w:Zoom>90</w:Zoom>
          </w:WordDocument>
        </xml>
        <![endif]-->
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .page { max-width: 800px; margin: 0 auto; }
        </style>
      </head>
      <body>
        ${lines.join('\n')}
      </body>
      </html>
    `

    console.log('Creating Blob and initiating download...')
    const blob = new Blob(['\ufeff' + docContent], { type: 'application/msword' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    
    const baseName = uploadedFile.value.name.replace(/\.pdf$/i, '')
    a.download = `${baseName}.doc`
    a.click()
    URL.revokeObjectURL(url)

    isCompleted.value = true
    console.log('Conversion successful and file download triggered.')
  } catch (error) {
    console.error('Error during conversion:', error)
    alert('Error al convertir el archivo PDF a Word: ' + (error.message || error))
  } finally {
    isProcessing.value = false
  }
}

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

const resetAll = () => {
  uploadedFile.value = null
  isCompleted.value = false
  isProcessing.value = false
  pageCount.value = 0
  currentPageProcessed.value = 0
  pdfDocRef.value = null
}

onMounted(() => {
  window.addEventListener('paste', handlePaste)
})

onUnmounted(() => {
  window.removeEventListener('paste', handlePaste)
})
</script>

<style scoped>
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}
</style>
